'use client';
import React from 'react';

const steps = [
  {
    number: '1',
    title: 'Site Assessment',
    desc: 'Our engineers visit your site to evaluate dimensions, soil conditions, access points, and vehicle count requirements.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 4L6 11v18l14 7 14-7V11L20 4z" fill="#E0F5FD" stroke="#00AAE2" strokeWidth="1.6" strokeLinejoin="round"/>
        <path d="M6 11l14 7M20 36V18M34 11l-14 7" stroke="#00AAE2" strokeWidth="1.6" strokeLinecap="round"/>
        <circle cx="20" cy="18" r="3" fill="#00AAE2"/>
      </svg>
    ),
  },
  {
    number: '2',
    title: 'Design & Manufacturing',
    desc: 'We create a tailored engineering design and precision-manufacture all structural components at our facility.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="5" width="30" height="30" rx="4" fill="#E0F5FD" stroke="#00AAE2" strokeWidth="1.6"/>
        <path d="M11 20h18M11 13h10M11 27h13" stroke="#062543" strokeWidth="1.6" strokeLinecap="round"/>
        <circle cx="28" cy="27" r="3" fill="#00AAE2"/>
      </svg>
    ),
  },
  {
    number: '3',
    title: 'Installation & Handover',
    desc: 'Our certified team installs the system on-site, completes rigorous testing, and provides full operator training.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="14" fill="#E0F5FD" stroke="#00AAE2" strokeWidth="1.6"/>
        <circle cx="20" cy="20" r="9" fill="#00AAE2" opacity="0.15"/>
        <path d="M13 20l5 5 10-10" stroke="#00AAE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

export default function ProcessSection() {
  return (
    <section
      id="process"
      className="py-16 bg-white"
      style={{ fontFamily: 'Ubuntu, sans-serif' }}
    >
      <div className="max-w-[1100px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="flex justify-center mb-4">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="3" fill="#062543"/>
              <circle cx="8" cy="10" r="2" fill="#062543" opacity="0.5"/>
              <circle cx="28" cy="10" r="2" fill="#062543" opacity="0.5"/>
              <circle cx="8" cy="26" r="2" fill="#062543" opacity="0.3"/>
              <circle cx="28" cy="26" r="2" fill="#062543" opacity="0.3"/>
              <circle cx="18" cy="5" r="1.5" fill="#062543" opacity="0.4"/>
              <circle cx="18" cy="31" r="1.5" fill="#062543" opacity="0.4"/>
              <circle cx="5" cy="18" r="1.5" fill="#062543" opacity="0.4"/>
              <circle cx="31" cy="18" r="1.5" fill="#062543" opacity="0.4"/>
            </svg>
          </div>
          <h2
            className="text-[#062543] font-bold mb-3"
            style={{ fontSize: '28px', lineHeight: '38px' }}
          >
            How We Work
          </h2>
          <p
            className="text-[#6B7280] max-w-[420px] mx-auto"
            style={{ fontSize: '14px', lineHeight: '24px' }}
          >
            A clear, structured process that ensures every project is delivered on time and to the highest standards.
          </p>
        </div>

        {/* Steps row — always horizontal, 3 columns */}
        <div className="relative grid grid-cols-3 gap-2 md:gap-0 items-start">
          {steps?.map((step, index) => (
            <React.Fragment key={step?.number}>
              {/* Step card */}
              <div className="flex flex-col items-center text-center px-1 md:px-4">
                {/* Number badge + icon circle */}
                <div className="relative mb-3 md:mb-6">
                  {/* Outer dashed circle */}
                  <div
                    className="w-[72px] h-[72px] md:w-[110px] md:h-[110px] rounded-full flex items-center justify-center"
                    style={{
                      border: '2px dashed #CBD5E1',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {/* Inner solid circle */}
                    <div
                      className="w-[52px] h-[52px] md:w-[82px] md:h-[82px] rounded-full flex items-center justify-center bg-white"
                      style={{ boxShadow: '0 4px 20px rgba(0,170,226,0.12)' }}
                    >
                      {step?.icon}
                    </div>
                  </div>
                  {/* Step number badge */}
                  <span
                    className="absolute top-0 left-0 w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ fontSize: '10px', backgroundColor: '#062543' }}
                  >
                    {step?.number}
                  </span>
                </div>

                {/* Title */}
                <h3
                  className="text-[#062543] font-semibold mb-1 md:mb-3"
                  style={{ fontSize: '12px', lineHeight: '18px' }}
                  // md override via inline won't work — use className for md
                >
                  <span className="hidden md:inline" style={{ fontSize: '17px', lineHeight: '24px' }}>{step?.title}</span>
                  <span className="md:hidden">{step?.title}</span>
                </h3>

                {/* Description — hidden on mobile to save space */}
                <p
                  className="text-[#6B7280] hidden md:block"
                  style={{ fontSize: '14px', lineHeight: '22px', maxWidth: '230px' }}
                >
                  {step?.desc}
                </p>
                {/* Short desc on mobile */}
                <p
                  className="text-[#6B7280] md:hidden"
                  style={{ fontSize: '10px', lineHeight: '15px' }}
                >
                  {step?.desc?.split(' ')?.slice(0, 8)?.join(' ')}…
                </p>
              </div>

              {/* Dashed curved connector (between steps, not after last) */}
              {index < steps?.length - 1 && (
                <div
                  className="absolute hidden md:flex items-center justify-center"
                  style={{
                    left: `calc(${(index + 1) * 33.33}% - 40px)`,
                    top: '40px',
                    width: '80px',
                  }}
                >
                  <svg width="80" height="30" viewBox="0 0 80 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0 20 Q20 2 40 15 Q60 28 80 10"
                      stroke="#CBD5E1"
                      strokeWidth="1.8"
                      strokeDasharray="5 4"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
